import { App, Button, Drawer, Form, Input, Select, Space, TreeSelect } from 'antd';
import { useCallback } from 'react';
import { ApiError } from '@/services/api/client';
import * as departmentsApi from '@/services/api/departments';
import type { DepartmentTreeNode } from '@/types/department';
import { collectDescendantIds, toParentTreeSelectData } from '@/utils/department-tree';

export type DepartmentFormMode = 'create' | 'createChild' | 'edit';

export interface DepartmentFormValues {
  name: string;
  code: string;
  parentId?: string;
  managerId?: string;
}

interface ManagerOption {
  value: string;
  label: string;
}

interface DepartmentFormDrawerProps {
  open: boolean;
  mode: DepartmentFormMode;
  tree: DepartmentTreeNode[];
  managerOptions: ManagerOption[];
  editingDepartment: DepartmentTreeNode | null;
  parentIdForCreate?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function DepartmentFormDrawer({
  open,
  mode,
  tree,
  managerOptions,
  editingDepartment,
  parentIdForCreate,
  onClose,
  onSuccess,
}: DepartmentFormDrawerProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<DepartmentFormValues>();

  const isEdit = mode === 'edit';
  const title =
    mode === 'edit' ? '编辑部门' : mode === 'createChild' ? '新增子部门' : '新增部门';

  const disabledParentIds = new Set<string>();
  if (isEdit && editingDepartment) {
    collectDescendantIds(editingDepartment).forEach((id) => disabledParentIds.add(id));
  }

  const parentTreeData = toParentTreeSelectData(tree, { disabledIds: disabledParentIds });

  const applyFormValues = useCallback(() => {
    if (isEdit && editingDepartment) {
      form.setFieldsValue({
        name: editingDepartment.name,
        code: editingDepartment.code,
        parentId: editingDepartment.parentId ?? undefined,
        managerId: editingDepartment.managerId ?? undefined,
      });
      return;
    }

    form.resetFields();
    if (mode === 'createChild' && parentIdForCreate) {
      form.setFieldsValue({ parentId: parentIdForCreate });
    }
  }, [editingDepartment, form, isEdit, mode, parentIdForCreate]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const code = values.code.trim();

    try {
      if (isEdit && editingDepartment) {
        await departmentsApi.updateDepartment(editingDepartment.id, {
          name: values.name.trim(),
          code,
          parentId: values.parentId ?? null,
          managerId: values.managerId ?? null,
        });
        message.success('部门已更新');
      } else {
        await departmentsApi.createDepartment({
          name: values.name.trim(),
          code,
          parentId: values.parentId,
          managerId: values.managerId || undefined,
        });
        message.success('部门已创建');
      }
      onSuccess();
      onClose();
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '保存失败';
      message.error(text);
    }
  };

  const formKey =
    isEdit && editingDepartment ? `edit-${editingDepartment.id}` : `${mode}-${parentIdForCreate ?? 'root'}`;

  return (
    <Drawer
      title={title}
      open={open}
      onClose={onClose}
      width={480}
      destroyOnClose
      afterOpenChange={(visible) => {
        if (visible) {
          applyFormValues();
        }
      }}
      extra={null}
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" onClick={() => void handleSubmit()}>
            保存
          </Button>
        </Space>
      }
    >
      <Form key={formKey} form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="name"
          label="部门名称"
          rules={[
            { required: true, message: '请输入部门名称' },
            { max: 128, message: '最多 128 个字符' },
          ]}
        >
          <Input placeholder="例如：综合办公室" />
        </Form.Item>
        <Form.Item
          name="code"
          label="部门编码"
          rules={[
            { required: true, message: '请输入部门编码' },
            { max: 64, message: '最多 64 个字符' },
          ]}
        >
          <Input placeholder="例如：GENERAL_OFFICE" />
        </Form.Item>
        <Form.Item name="parentId" label="上级部门">
          <TreeSelect
            allowClear
            placeholder="不选则为根部门"
            treeData={parentTreeData}
            treeDefaultExpandAll
            disabled={mode === 'createChild' && Boolean(parentIdForCreate)}
          />
        </Form.Item>
        <Form.Item name="managerId" label="部门负责人">
          <Select
            allowClear
            showSearch
            placeholder="可选"
            optionFilterProp="label"
            options={managerOptions}
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
